import axios from 'axios';
import {getAuth} from 'firebase/auth';

const INFLUX_API_URL = "https://influxapi.smartchargecontroller.ch/v1";
const INFLUX_LOGS_URL = "https://influxapi.smartchargecontroller.ch/v1/getAggregatedLogs";
const DEFAULT_BUCKET = "sccmetrics";

const DOMAIN_ERROR_MESSAGES = {
  STATS_DATA_UNAVAILABLE: 'Unable to fetch data. Please try again.',
  STATS_QUERY_TIMEOUT: 'Query timed out. Please try a shorter time range.',
  STATS_UNAUTHORIZED: 'Authentication failed. Please sign in again.',
};

const toInfluxError = (error, fallbackCode = 'STATS_DATA_UNAVAILABLE') => {
  let code = fallbackCode;

  if (error?.code === 'ECONNABORTED') {
    code = 'STATS_QUERY_TIMEOUT';
  } else if (error?.response?.status === 401 || error?.response?.status === 403) {
    code = 'STATS_UNAUTHORIZED';
  }

  const message = DOMAIN_ERROR_MESSAGES[code] || error?.message || 'Unknown error';
  return {
    code,
    message,
    status: error?.response?.status || null,
    cause: error || null,
  };
};

const ok = (data = null) => ({ok: true, data, error: null});
const fail = (error) => ({ok: false, data: null, error});

const logInfluxError = (context, errorInfo) => {
  console.error(`[influxApi:${context}] ${errorInfo.code}: ${errorInfo.message}`, errorInfo.cause || errorInfo);
};

/**
 * Fetch time series data from InfluxDB for a given chart
 * @param {string} edgeId - Edge identifier
 * @param {object} chart - Chart configuration with filters
 * @param {number} nrOfDataPoints - Number of data points to fetch
 * @param {string} start - ISO timestamp start
 * @param {string} stop - ISO timestamp end
 * @returns {Promise<{ok: boolean, data: Array, error: object}>}
 */
export const fetchTimeSeriesData = async (edgeId, chart, nrOfDataPoints, start, stop) => {
  try {
    const idTokenResult = await getAuth().currentUser.getIdTokenResult();

    const postData = {
      id: edgeId,
      filters: chart.filters,
      nrOfDataPoints,
      start,
      stop,
    };

    const headers = {
      'Authorization': 'Bearer ' + idTokenResult.token,
    };

    const response = await axios.post(`${INFLUX_API_URL}?bucket=${DEFAULT_BUCKET}`, postData, {
      headers,
      timeout: 30000, // 30 second timeout
    });

    if (!response.data || response.data.length === 0) {
      return ok([]);
    }

    // Transform response into chart-ready format
    const dict = {};
    let sortByKey = "";

    // Find the last tag key in first object
    if (response.data.length > 0) {
      for (const key in response.data[0]) {
        if (key.includes("tags_")) {
          sortByKey = key;
        }
      }
    }

    for (const val of response.data) {
      const value = Math.round(val._value * 100) / 100;
      dict[val._time] = {
        ...dict[val._time],
        _time: val._time,
        [val[sortByKey]]: value
      };
    }

    const dataKeys = [...new Set(response.data.map(item => item[sortByKey]))];
    const chartData = Object.values(dict).sort((a, b) => new Date(a._time) - new Date(b._time));

    return ok({
      data: chartData,
      dataKeys,
      sortByKey,
    });
  } catch (error) {
    const errorInfo = toInfluxError(error);
    logInfluxError('fetchTimeSeriesData', errorInfo);
    return fail(errorInfo);
  }
};

/**
 * Fetch aggregated logs for the given time range
 * @param {string} edgeId - Edge identifier
 * @param {string} start - ISO timestamp start
 * @param {string} stop - ISO timestamp end
 * @param {number} limit - Maximum number of logs (default 20)
 * @returns {Promise<{ok: boolean, data: Array, error: object}>}
 */
export const fetchAggregatedLogs = async (edgeId, start, stop, limit = 20) => {
  try {
    const idTokenResult = await getAuth().currentUser.getIdTokenResult();

    const postData = {
      id: edgeId,
      start,
      stop,
      limit,
    };

    const headers = {
      'Authorization': 'Bearer ' + idTokenResult.token,
    };

    const response = await axios.post(INFLUX_LOGS_URL, postData, {
      headers,
      timeout: 30000,
    });

    return ok(response.data || []);
  } catch (error) {
    const errorInfo = toInfluxError(error);
    logInfluxError('fetchAggregatedLogs', errorInfo);
    return fail(errorInfo);
  }
};

